import { useContext, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import Checkbox from '../ui/elements/checkbox'
import Button from '../ui/elements/button'
import FileListItem from './file-list-item'
import { CreateProjectDispatch } from './create-project'
import Blur from '../ui/shared/blur'
import { FixedSizeList as List } from 'react-window'
import WindowListener from '../window-listener'
import AutoSizer from 'react-virtualized-auto-sizer'
import { fileListItems } from './file-list-utils'

export default function FileList({
  files,
  metaData,
  lastSelected,
  status,
  metaDataInitialized,
  groupedFileList,
  cameraProperties,
  addFilesLabel,
  allowChanges,
}) {
  const dispatch = useContext(CreateProjectDispatch)
  const individualFileUpload = useRef(null)
  const fileValues = Object.values(files)
  const removeDisabled =
    !allowChanges || !fileValues.some(file => file.isSelected === true)
  const isUploading = status === 'uploading'
  const isDoneUploading = status === 'success'
  const inEditMode = !isUploading && !isDoneUploading
  const renderFileList = fileListItems(
    groupedFileList,
    files,
    cameraProperties,
    lastSelected,
    metaData
  )

  const numberOfSelectedFiles = fileValues.filter(file => file.isSelected)
    .length
  const numberOfSelectedCaptures =
    groupedFileList &&
    Object.values(groupedFileList).filter(groupFiles =>
      groupFiles.every(fileId => files[fileId] && files[fileId].isSelected)
    ).length
  const allSelected = numberOfSelectedFiles === fileValues.length

  const imageInfoText =
    numberOfSelectedFiles === numberOfSelectedCaptures
      ? numberOfSelectedCaptures === 1
        ? 'image'
        : 'images'
      : numberOfSelectedCaptures === 1
      ? 'capture'
      : 'captures'

  useEffect(() => {
    if (
      fileValues.length === 0 ||
      (lastSelected && !groupedFileList[lastSelected])
    ) {
      dispatch({
        type: 'SET_LAST_SELECTED',
        payload: null,
      })
    }
  }, [dispatch, fileValues.length, groupedFileList, lastSelected])

  const handleAllSelected = () => {
    const allIds = Object.keys(files)
    if (!allSelected) {
      dispatch({ type: 'SELECT_FILES', payload: allIds })
    } else {
      dispatch({ type: 'UNSELECT_FILES', payload: allIds })
    }
  }

  const handleRemoveSelected = event => {
    event.preventDefault()
    if (
      window.confirm(
        `Do you want to remove ${numberOfSelectedCaptures} ${imageInfoText}`
      )
    ) {
      dispatch({ type: 'REMOVE_FILES' })
    }
  }

  const ListItem = ({ index, style }) => {
    const currentFileListItem = renderFileList[index]

    if (currentFileListItem) {
      const {
        groupKey,
        currentSelected,
        isSelected,
        isItemUploaded,
        hasGeoRef,
        isMissingImagesInCapture,
        isMissingFilesInCapture,
        name,
      } = currentFileListItem
      return (
        <div style={style}>
          <FileListItem
            name={name}
            isSelected={isSelected}
            index={index}
            status={status}
            isItemUploaded={isItemUploaded}
            cameraProperties={cameraProperties}
            currentSelected={currentSelected}
            validationMessage={
              !hasGeoRef
                ? 'Image is not georeferenced'
                : isMissingImagesInCapture
                ? `Missing ${
                    cameraProperties.imagesPerCapture -
                    currentFileListItem.fileList.length
                  } images for this capture`
                : isMissingFilesInCapture
                ? `Missing ${
                    currentFileListItem.fileList.filter(
                      fileId => !files[fileId].file
                    ).length
                  } files in this capture`
                : null
            }
            onClick={event =>
              selectFileToPreview(event, groupKey, isSelected, currentSelected)
            }
            onSelect={() => selectItems(groupKey, isSelected)}
          />
        </div>
      )
    } else {
      return null
    }

    function selectFileToPreview(event, groupKey, isSelected, currentSelected) {
      event.preventDefault()

      if (metaData) {
        if (currentSelected) {
          dispatch({
            type: 'SET_LAST_SELECTED',
            payload: null,
          })
        } else {
          dispatch({
            type: 'SET_LAST_SELECTED',
            payload: groupKey,
          })
          if (event.shiftKey) {
            selectItems(groupKey, isSelected, true)
          }
        }
      }
    }

    function selectItems(groupKey, isSelected, multiple) {
      let selectedIds

      if (multiple && lastSelected) {
        const lastSelectedIndex = Object.keys(groupedFileList).findIndex(
          key => key === lastSelected
        )
        const currentIndex = Object.keys(groupedFileList).findIndex(
          key => key === groupKey
        )

        selectedIds = Object.values(groupedFileList)
          .slice(
            Math.min(lastSelectedIndex, currentIndex),
            Math.max(lastSelectedIndex, currentIndex) + 1
          )
          .flatMap(fileList => fileList.flatMap(fileId => fileId))
      } else {
        selectedIds = groupedFileList[groupKey]
      }

      if (!isSelected) {
        dispatch({ type: 'SELECT_FILES', payload: selectedIds })
      } else {
        dispatch({ type: 'UNSELECT_FILES', payload: selectedIds })
      }
    }
  }

  return (
    <WindowListener onKeydown={onKeydown}>
      <div className="tw-w-1/5 tw-bg-white tw-max-w-xs tw-z-10 tw-h-full">
        {isDoneUploading && <Blur />}
        <input
          type="file"
          multiple
          accept=".tif, .tiff, .jpg, .jpeg"
          id="individual-files-input"
          ref={individualFileUpload}
          style={{ display: 'none' }}
          onChange={e => {
            const files = {}
            Array.from(e.target.files).forEach(file => {
              files[file.name] = {
                file,
                isSelected: false,
                uploadStatus: 'idle',
                id: file.name,
              }
            })
            dispatch({ type: 'UPDATE_FILES', payload: files })
          }}
        />
        <div className="tw-px-5 tw-pt-5">
          <Button
            icon="plus"
            size="medium"
            onClick={() => {
              individualFileUpload.current.value = ''
              individualFileUpload.current.click()
            }}
            action="primary-light"
            disabled={!inEditMode}>
            {addFilesLabel}
          </Button>
          <div className="tw-px-5 tw-py-3 tw-mt-4 tw-border-b tw-border-solid tw-border-t-0 tw-border-r-0 tw-border-l-0 tw-border-system-grey-200 tw--mx-5">
            <Checkbox
              active={allSelected && inEditMode && numberOfSelectedFiles > 0}
              onChange={handleAllSelected}
              disabled={Object.keys(files).length === 0 || !inEditMode}>
              <span className="tw-custom-uppercase tw-text-system-grey-400">{`${
                allSelected ? 'Deselect all' : 'Select all'
              }`}</span>
            </Checkbox>
          </div>
        </div>
        {(metaDataInitialized && metaData && renderFileList && (
          <div style={{ height: 'calc(100% - 201px)' }}>
            <AutoSizer>
              {({ width, height }) => (
                <List
                  width={width}
                  height={height}
                  itemCount={Object.keys(renderFileList).length || 0}
                  itemSize={46}>
                  {ListItem}
                </List>
              )}
            </AutoSizer>
          </div>
        )) || (
          <p className="tw-m-0 tw-p-5">
            {`${
              !metaDataInitialized ? 'Examining images...' : 'No images added'
            }`}
          </p>
        )}
        <div className="tw-p-5 tw-custom-border-top">
          <Button
            className="tw-w-full"
            icon="trash"
            size="medium"
            onClick={handleRemoveSelected}
            action="danger"
            disabled={removeDisabled || !inEditMode}
            title={
              !allowChanges
                ? "Images can't be removed from an upload that has been started"
                : null
            }>
            {removeDisabled || !inEditMode
              ? 'Remove'
              : `Remove ${numberOfSelectedCaptures} ${imageInfoText}`}
          </Button>
        </div>
      </div>
    </WindowListener>
  )

  function onKeydown(e) {
    if (e.code === 'ArrowDown') {
      selectNext(1)
      e.preventDefault()
    } else if (e.code === 'ArrowUp') {
      selectNext(-1)
      e.preventDefault()
    } else if (e.code === 'Space') {
      if (lastSelected) {
        dispatch({
          type: groupedFileList[lastSelected].every(
            fileId => files[fileId].isSelected
          )
            ? 'UNSELECT_FILES'
            : 'SELECT_FILES',
          payload: groupedFileList[lastSelected].map(
            fileId => files[fileId].id
          ),
        })
      }
    }

    function selectNext(dir) {
      if (lastSelected) {
        const currentIndex = Object.keys(groupedFileList).findIndex(
          key => key === lastSelected
        )
        const nextIndex = currentIndex + dir
        if (nextIndex >= 0 && nextIndex < Object.keys(groupedFileList).length) {
          const nextSelection = Object.keys(groupedFileList)[nextIndex]
          dispatch({
            type: 'SET_LAST_SELECTED',
            payload: nextSelection,
          })
        }
      }
    }
  }
}

FileList.propTypes = {
  files: PropTypes.object.isRequired,
  status: PropTypes.string,
  groupedFileList: PropTypes.object,
  metaData: PropTypes.object,
  lastSelected: PropTypes.string,
  metaDataInitialized: PropTypes.bool,
  cameraProperties: PropTypes.object,
  addFilesLabel: PropTypes.string,
  allowChanges: PropTypes.bool,
}
